import { useState, useEffect } from 'react';
import { useAuth } from '@/shared/auth/AuthContext';
import api from '@/shared/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Checkbox } from '@/shared/ui/checkbox';
import { UserPlus, Trash2, Shield, ShieldCheck, Wrench, HardHat, Briefcase, KeyRound, Pencil, Store, MailWarning, Layers, UserCheck, UserX, CircleHelp } from 'lucide-react';
import useBulkSelection from '@/shared/hooks/useBulkSelection';
import useBulkAction from '@/shared/hooks/useBulkAction';
import BulkEditDialog from '@/shared/components/BulkEditDialog';
import BulkResultDialog from '@/shared/components/BulkResultDialog';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import USER_BULK_ACTIONS from '@/features/admin/bulkActions';
import { genererMotDePasse } from '@/shared/components/BoutonMotDePasse';
import { getDelegations, majDelegations } from '@/shared/api/delegations';
import PrivilegesDelegues from '@/features/admin/components/PrivilegesDelegues';
import { classerErreur } from '@/shared/api/erreurs';

// ⚠️ LA CLE EST TECHNIQUE, LE LIBELLE EST HUMAIN (lot I, item I5, retour du 30/08).
//
// David appelle ce profil un « gestionnaire ». La cle, elle, reste `commercial` : elle
// porte les treize gardes de `backend/app/core/authz.py` et la valeur `role` des comptes
// deja crees en base. La renommer serait une migration touchant les droits des six roles,
// et priverait d'acces quiconque a un compte existant. On renomme ce qu'on LIT, jamais ce
// qu'on COMPARE.
const ROLE_CONFIG = {
  admin: { label: 'Admin', icon: Shield, color: 'bg-red-100 text-red-700' },
  super_admin: { label: 'Super admin', icon: ShieldCheck, color: 'bg-neutral-900 text-white' },
  commercial: { label: 'Gestionnaire', icon: Briefcase, color: 'bg-blue-100 text-blue-700' },
  installateur: { label: 'Installateur', icon: Wrench, color: 'bg-emerald-100 text-emerald-700' },
  poseur: { label: 'Poseur', icon: HardHat, color: 'bg-teal-100 text-teal-700' },
  distributeur: { label: 'Distributeur', icon: Store, color: 'bg-violet-100 text-violet-700' },
};

// ⚠️ UN ROLE INCONNU S AFFICHE TEL QUEL. IL NE SE DEGUISE PLUS (C6, 07/09/2026).
//
// Cette ligne disait `ROLE_CONFIG[u.role] || ROLE_CONFIG.commercial`, et ce repli a
// fait exactement ce qu'un repli silencieux finit toujours par faire : il a transforme
// une donnee JUSTE en ecran FAUX. La preproduction repondait bien `role: "poseur"`
// pour boubacar+poseur@flairdigital.fr ; l'ecran, lui, affichait « Gestionnaire »,
// pastille bleue et mallette comprises. Rien ne le signalait — c'est la definition
// meme d'une panne silencieuse, et elle a coute une recette.
//
// Le repli honnete est celui que `PrivilegesDelegues` tient deja pour les libelles de
// role (`LIBELLES_ROLES[role] || role`) et que `libellesCapacites.js` documente : mieux
// vaut un libelle aride qu'un libelle FAUX. Un role que personne n'a encore nomme ici
// s'affiche donc sous sa cle technique, en gris, avec un point d'interrogation. C'est
// laid, et c'est le but : cela se voit a l'oeil nu le jour ou un role apparait cote
// serveur sans que cet ecran l'apprenne.
//
// AJOUTER UN ROLE ICI N'OUVRE AUCUN DROIT. Ce dictionnaire NOMME, il n'autorise pas :
// la matrice vit dans `backend/app/core/authz.py`, la liste des roles valides dans
// `TENANT_ROLES` (`backend/app/core/security.py`).
const roleAffiche = (role) => ROLE_CONFIG[role] || {
  label: role || 'Rôle inconnu',
  icon: CircleHelp,
  color: 'bg-neutral-100 text-neutral-600',
};

// Onglets de la vue utilisateurs (wireframe D).
const TABS = [
  { key: 'all', label: 'Tous' },
  { key: 'commercial', label: 'Gestionnaires' },
  { key: 'installateur', label: 'Installateurs' },
  { key: 'distributeur', label: 'Distributeurs' },
];

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [form, setForm] = useState({ name: '', email: '', role: 'installateur' });
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '' });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [confirmPending, setConfirmPending] = useState(null);

  // LES PRIVILEGES DELEGUES VIVENT ICI DEPUIS LE 03/09, et non plus dans un ecran a
  // part. Boubacar a recette la page dediee et rejete sa SURFACE : « ca n'est pas une
  // page en plus, c'est un choix de privileges qu'on doit pouvoir faire directement
  // dans la partie utilisateurs ». Le mecanisme, lui, n'a pas bouge : memes routes,
  // meme liste blanche, meme plafond par role.
  //
  // `lignesDelegation` est ce que le SERVEUR publie, role par role : ce qui lui est
  // delegable, ce qui lui est ouvert, et ce qu'il garde de toute facon. Le front n'en
  // recopie rien, il ne peut donc pas proposer une case que le serveur refuserait.
  const [lignesDelegation, setLignesDelegation] = useState(null);
  const [refusDelegation, setRefusDelegation] = useState(null);
  const [privileges, setPrivileges] = useState(new Set());
  const [clePrivileges, setClePrivileges] = useState(null);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const fetchDelegations = async () => {
    try {
      const data = await getDelegations();
      setLignesDelegation(data.roles || []);
      setRefusDelegation(null);
    } catch (err) {
      // Un refus n'est pas une absence de privilege. Sans ce cas, un 403 sur la lecture
      // afficherait « aucun privilege delegable » : une reponse metier la ou il n'y a
      // qu'une porte fermee (`frontend/CLAUDE.md`, « un ecran ne masque pas un refus »).
      setLignesDelegation([]);
      setRefusDelegation(classerErreur(err).titre);
    }
  };

  useEffect(() => { fetchUsers(); fetchDelegations(); }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Nom et email sont obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      // LE VERDICT D'ENVOI SE LIT, IL NE SE SUPPOSE PAS.
      //
      // Le serveur remonte `invitation_envoyee` précisément pour cet écran : l'admin qui
      // vient de créer le compte est la seule personne en mesure de réagir tout de
      // suite. Cet écran l'ignorait et affichait « Invitation envoyée » quoi qu'il
      // arrive. Mesuré le 27/08 en pré-production : le compte d'un collaborateur a été
      // créé, l'invitation n'est jamais partie (`statut : skipped`), l'écran a dit que
      // si, et il a fallu lui passer un mot de passe à la main.
      //
      // Un compte sans invitation n'est pas à moitié créé : il est INUTILISABLE, son
      // détenteur ne peut pas définir de mot de passe, donc pas se connecter.
      const { data } = await api.post('/auth/invite', form);
      if (data?.invitation_envoyee) {
        toast.success(`Compte ajouté, invitation envoyée à ${form.email}`);
      } else {
        toast.warning(
          `Compte ajouté, mais l'invitation n'est PAS partie (${data?.invitation_statut || 'raison inconnue'}). `
          + `${form.email} ne peut pas se connecter tant que l'invitation n'est pas renvoyée.`,
          { duration: 12000 });
      }
      setForm({ name: '', email: '', role: 'installateur' });
      setShowForm(false);
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Erreur lors de l'ajout du compte");
    }
    setSubmitting(false);
  };

  // Desactiver plutot que supprimer : le compte garde ses signatures, et l'acces
  // tombe des la requete suivante (le controle vit dans get_current_user cote serveur).
  const handleToggleActive = async (u) => {
    const desactive = u.active === false;
    try {
      await api.put(`/users/${u.id}/toggle-active`);
      toast.success(desactive ? `${u.name} a de nouveau acces` : `${u.name} n'a plus acces`);
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur');
    }
  };

  const handleDelete = (userId, userName) => setConfirmPending({
    title: 'Supprimer le compte',
    targetLabel: 'Compte ciblé',
    target: userName,
    warning: "Suppression définitive : la personne perd immédiatement l'accès à "
      + "l'application, sans restauration possible depuis l'interface.",
    confirmLabel: 'Supprimer',
    tone: 'danger',
    onConfirm: async () => {
      try {
        await api.delete(`/users/${userId}`);
        toast.success('Compte supprimé');
        fetchUsers();
      } catch (err) {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la suppression');
      }
    },
  });

  const handleResetPassword = async (userId, userName) => {
    // Le mot de passe est PROPOSÉ, pas exigé : `window.prompt` avec un champ vide
    // laissait l'admin inventer le sien, et c'est ainsi qu'on obtient « Temp26 ». La
    // valeur pré-remplie est générée par le même code que les trois autres écrans,
    // donc la même politique — voir `BoutonMotDePasse`. Elle reste modifiable : un
    // administrateur qui tient à la sienne n'est pas bloqué, il est seulement obligé
    // de l'écrire par-dessus une proposition correcte.
    const propose = genererMotDePasse();
    const newPassword = window.prompt(
      `Nouveau mot de passe pour ${userName} (au moins 10 caractères).\n`
      + 'Une proposition est déjà remplie — copiez-la avant de valider, '
      + 'elle ne sera plus affichée.',
      propose);
    if (!newPassword) return;
    if (newPassword.length < 10) { toast.error('Le mot de passe doit faire au moins 10 caracteres'); return; }
    try {
      await api.put(`/users/${userId}/reset-password`, { password: newPassword });
      toast.success(`Mot de passe réinitialisé pour ${userName}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur');
    }
  };

  const openEditUser = (u) => {
    setEditForm({ name: u.name, email: u.email, role: u.role });
    setEditUser(u);
  };

  // LA COCHE SUIT LE ROLE AFFICHE dans la boite, pas celui enregistre en base : changer
  // le role d'un compte et regler ses privileges se font dans le meme geste, et l'ecran
  // doit alors montrer les privileges du role VISE. La cle porte aussi l'arrivee des
  // donnees du serveur, sinon une boite ouverte avant la reponse resterait vide a jamais.
  const cleCourante = editUser
    ? `${editUser.id}:${editForm.role}:${lignesDelegation ? 'pret' : 'attente'}`
    : null;

  useEffect(() => {
    if (!cleCourante || cleCourante === clePrivileges) return;
    const ligne = (lignesDelegation || []).find(l => l.role === editForm.role);
    setPrivileges(new Set(ligne?.accordees || []));
    setClePrivileges(cleCourante);
  }, [cleCourante, clePrivileges, editForm.role, lignesDelegation]);

  const basculerPrivilege = (capacite) => setPrivileges(actuel => {
    const suivant = new Set(actuel);
    if (suivant.has(capacite)) suivant.delete(capacite); else suivant.add(capacite);
    return suivant;
  });

  // DEUX ECRITURES, ET ELLES NE PORTENT PAS SUR LA MEME CHOSE. Le compte est ecrit sur
  // `/users/{id}` ; les privileges le sont sur `/admin/delegations`, parce qu'ils sont
  // ranges PAR ROLE et par sous-compte, jamais par personne. Les envoyer ensemble
  // demanderait au serveur de deviner laquelle des deux regles il applique.
  //
  // La seconde n'est envoyee que si la coche a bouge : un PUT systematique reecrirait la
  // table des delegations du sous-compte a chaque simple renommage de compte.
  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editUser) return;

    try {
      const payload = { name: editForm.name, email: editForm.email, role: editForm.role };
      await api.put(`/users/${editUser.id}`, payload);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
      return;
    }

    const ligne = (lignesDelegation || []).find(l => l.role === editForm.role);
    const avant = [...(ligne?.accordees || [])].sort().join('|');
    const apres = [...privileges].sort().join('|');
    let privilegesEcrits = false;

    if (ligne && avant !== apres) {
      try {
        await majDelegations({ [editForm.role]: [...privileges] });
        await fetchDelegations();
        privilegesEcrits = true;
      } catch (err) {
        // Le compte EST enregistre : le taire ferait croire que rien n'est parti, et
        // l'admin recommencerait tout. On nomme precisement ce qui n'a pas eu lieu.
        toast.warning(
          "Le compte est enregistré, mais les privilèges n'ont PAS été modifiés"
          + ` (${err.response?.data?.detail || 'erreur inconnue'}).`,
          { duration: 10000 });
        setEditUser(null);
        fetchUsers();
        return;
      }
    }

    toast.success(privilegesEcrits
      ? 'Compte et privilèges mis à jour'
      : 'Utilisateur mis à jour');
    setEditUser(null);
    fetchUsers();
  };

  const countFor = (key) => key === 'all' ? users.length : users.filter(u => u.role === key).length;
  const visibleUsers = activeTab === 'all' ? users : users.filter(u => u.role === activeTab);

  // Le compte courant n'est pas sélectionnable : ni suppression ni changement de rôle
  // sur soi-même (le second se retournerait en perte d'accès immédiate).
  const selectableUsers = visibleUsers.filter(u => u.id !== currentUser?.id);
  // Hooks déclarés avant le garde-fou de rôle : l'ordre des hooks doit être stable
  // à chaque rendu, même quand la page refuse l'accès.
  const bulkSel = useBulkSelection(selectableUsers);
  const bulk = useBulkAction(() => { fetchUsers(); bulkSel.clear(); });
  const userLabel = (id) => {
    const u = users.find(x => x.id === id);
    return u ? `${u.name} (${u.email})` : id;
  };

  // Le catalogue des actions vit dans `features/admin/bulkActions.js`.
  const runBulk = async (code, value, def) => {
    setBulkOpen(false);
    await bulk.run(() => def.run(bulkSel.ids, value), {
      alwaysReport: def.alwaysReport,
      success: r => `${r.ok} compte${r.ok > 1 ? 's' : ''} traité${r.ok > 1 ? 's' : ''}`,
    });
  };

  if (currentUser?.role !== 'admin') {
    return <div className="p-8 text-center text-neutral-500">Acces reserve aux administrateurs.</div>;
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Gestion des utilisateurs</h1>
          <p className="text-sm text-neutral-500 mt-0.5">{users.length} compte{users.length > 1 ? 's' : ''} enregistré{users.length > 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          {bulkSel.count > 0 && (
            <Button onClick={() => setBulkOpen(true)} data-testid="open-bulk-btn">
              <Layers className="w-4 h-4 mr-2" />
              Édition groupée ({bulkSel.count})
            </Button>
          )}
          <Button
            variant={bulkSel.count > 0 ? 'outline' : 'default'}
            onClick={() => setShowForm(!showForm)}
            data-testid="add-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Ajouter un compte
          </Button>
        </div>
      </div>

      {showForm && (
        <Card data-testid="create-user-form">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Ajouter un compte</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInvite} className="space-y-4">
              <p className="text-[13px] text-neutral-500">
                L'utilisateur recevra un email pour définir lui-même son mot de passe. Aucun mot de passe à saisir ici.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-neutral-500">Nom complet *</Label>
                  <Input
                    className="h-9"
                    placeholder="Ex: Pierre Martin"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    data-testid="create-user-name"
                  />
                </div>
                <div>
                  <Label className="text-xs text-neutral-500">Email *</Label>
                  <Input
                    className="h-9"
                    type="email"
                    placeholder="Ex: pierre@flexcore.energy"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    data-testid="create-user-email"
                  />
                </div>
                <div>
                  <Label className="text-xs text-neutral-500">Role *</Label>
                  <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                    <SelectTrigger className="h-9" data-testid="create-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="installateur">Installateur</SelectItem>
                      <SelectItem value="commercial">Gestionnaire</SelectItem>
                      <SelectItem value="distributeur">Distributeur</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={submitting} data-testid="submit-create-user-btn">
                  {submitting ? 'Envoi...' : 'Ajouter le compte'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Onglets par rôle */}
      <div className="flex items-center gap-1 border-b border-neutral-200">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              activeTab === t.key
                ? 'border-neutral-900 text-neutral-900'
                : 'border-transparent text-neutral-400 hover:text-neutral-600'
            }`}
            data-testid={`users-tab-${t.key}`}
          >
            {t.label} <span className="text-neutral-300">({countFor(t.key)})</span>
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {loading ? (
          <p className="text-sm text-neutral-400 py-8 text-center">Chargement...</p>
        ) : visibleUsers.length === 0 ? (
          <p className="text-sm text-neutral-400 py-8 text-center">Aucun compte dans cette catégorie.</p>
        ) : (
          <>
          <div className="flex items-center gap-3 px-3 py-2 text-[11px] uppercase tracking-wider text-neutral-400 font-medium">
            <Checkbox
              checked={bulkSel.allSelected}
              onCheckedChange={bulkSel.toggleAll}
              data-testid="select-all-users"
            />
            <span>Tout sélectionner (hors votre compte)</span>
            {bulkSel.count > 0 && (
              <span className="normal-case tracking-normal text-neutral-500">
                · {bulkSel.count} choisi{bulkSel.count > 1 ? 's' : ''}
              </span>
            )}
          </div>
          {visibleUsers.map(u => {
            const rc = roleAffiche(u.role);
            const Icon = rc.icon;
            return (
              <div
                key={u.id}
                className="flex items-center gap-4 p-3.5 rounded-lg border border-neutral-200 bg-white hover:border-neutral-300 transition-colors"
                data-testid={`user-row-${u.id}`}
              >
                {u.id !== currentUser?.id ? (
                  <Checkbox
                    checked={bulkSel.isSelected(u.id)}
                    onCheckedChange={() => bulkSel.toggle(u.id)}
                    data-testid={`select-user-${u.id}`}
                  />
                ) : (
                  <span className="w-4" title="Votre propre compte n'est pas sélectionnable" />
                )}
                <div className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center text-sm font-semibold text-neutral-600">
                  {u.name?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900 truncate">{u.name}</p>
                  <p className="text-xs text-neutral-400 truncate">{u.email}</p>
                </div>
                {u.active === false && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-neutral-200 text-neutral-600"
                        title="Ce compte ne peut plus se connecter">
                    <UserX className="w-3 h-3" />
                    Désactivé
                  </span>
                )}
                {u.must_set_password && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700"
                        title="L'utilisateur n'a pas encore défini son mot de passe">
                    <MailWarning className="w-3 h-3" />
                    Invitation en attente
                  </span>
                )}
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${rc.color}`}
                      data-testid={`user-role-${u.id}`}
                      data-role={u.role || ''}>
                  <Icon className="w-3 h-3" />
                  {rc.label}
                </span>
                {u.id !== currentUser?.id && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEditUser(u)}
                      className="p-1.5 rounded-md text-neutral-300 hover:text-neutral-600 hover:bg-neutral-50 transition-colors"
                      title="Modifier"
                      data-testid={`edit-user-${u.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleResetPassword(u.id, u.name)}
                      className="p-1.5 rounded-md text-neutral-300 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                      title="Réinitialiser le mot de passe"
                      data-testid={`reset-password-${u.id}`}
                    >
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleToggleActive(u)}
                      className="p-1.5 rounded-md text-neutral-300 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                      title={u.active === false ? 'Réactiver ce compte' : 'Désactiver ce compte'}
                      data-testid={`toggle-active-user-${u.id}`}
                    >
                      {u.active === false
                        ? <UserCheck className="w-4 h-4" />
                        : <UserX className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDelete(u.id, u.name)}
                      className="p-1.5 rounded-md text-neutral-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          </>
        )}
      </div>

      {/* Actions en masse */}
      <BulkEditDialog
        open={bulkOpen}
        title="Édition groupée"
        noun="compte"
        count={bulkSel.count}
        actions={USER_BULK_ACTIONS}
        selectedItems={bulkSel.selectedItems}
        busy={bulk.busy}
        onCancel={() => setBulkOpen(false)}
        onSubmit={runBulk}
      />

      <BulkResultDialog
        report={bulk.report} onClose={bulk.clearReport}
        title="Résultat de l'action en masse" resolveLabel={userLabel}
      />

      <ConfirmDialog
        pending={confirmPending}
        onCancel={() => setConfirmPending(null)}
        onConfirm={async () => {
          const action = confirmPending?.onConfirm;
          setConfirmPending(null);
          await action?.();
        }}
      />

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={v => { if (!v) setEditUser(null); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifier l'utilisateur</DialogTitle></DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-3">
            <div>
              <Label className="text-xs text-neutral-500">Nom</Label>
              <Input className="h-9" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} data-testid="edit-user-name" />
            </div>
            <div>
              <Label className="text-xs text-neutral-500">Email</Label>
              <Input className="h-9" type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} data-testid="edit-user-email" />
            </div>
            <div>
              <Label className="text-xs text-neutral-500">Role</Label>
              <Select value={editForm.role} onValueChange={v => setEditForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="h-9" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="installateur">Installateur</SelectItem>
                  <SelectItem value="commercial">Gestionnaire</SelectItem>
                  <SelectItem value="distributeur">Distributeur</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Plus de choix d'installateurs rattachés : depuis le 01/10 (call avec David),
                un distributeur voit TOUS les produits de son sous-compte, CFT Sign comme
                Flexcore. Le réglage ne commandait plus rien ; un réglage sans effet fait
                croire qu'on a restreint quelque chose. */}
            {/* Le choix de privileges se fait ICI, sur le compte qu'on regarde deja :
                c'est tout l'objet du deplacement du 03/09. Il vaut pour le ROLE, et le
                bloc le dit — voir `components/PrivilegesDelegues.jsx`. */}
            <PrivilegesDelegues
              role={editForm.role}
              ligne={(lignesDelegation || []).find(l => l.role === editForm.role)}
              chargement={lignesDelegation === null}
              refus={refusDelegation}
              ouvertes={privileges}
              onBasculer={basculerPrivilege}
            />

            <Button type="submit" className="w-full" data-testid="submit-edit-user-btn">Enregistrer</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
